import React, { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import {
  Box,
  DialogTitle,
  Dialog,
  DialogContent,
  Button as MuiButton,
  Stack,
  type SelectChangeEvent,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import { ButtonVariantOption, LinkButton } from "../../components/Button";
import { Divider } from "@/components/Divider";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle, SectionTitle } from "@/components/Title";
import { FeedGrid } from "./components/FeedGrid";
import { ReceptionBookImportStatus } from "./components/ReceptionBookImportStatus";
import {
  type IRealEstateReceptionBookFeedPresenterProps,
  type IMultipleBookAcquireData,
  type UserSearchHistoryReceptionBookResponseType,
} from "./types";
import { useNavigate } from "react-router-dom";
import { formatNumberWithComma } from "@/utils/formatNumber";

import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";
import { SearchCondition } from "./components/SearchCondition";
import Tooltip from "@mui/material/Tooltip";
import { type NavigateOptions } from "react-router/dist/lib/context";
import { useFeatureFlags } from "@/configs/featureFlag";
import type { HandOffMessage } from "@/types/handOffMessage";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { AcquireBookFormUI } from "@/components/AcquireBook/AcquireBookFormUI";
import { usePrevious } from "@/hooks/usePrevious";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import deepEqual from "deep-equal";
import { type ISingleBookAcquireDialogData } from "../../types/singleBookAcquireDialog";
import { useMapServiceContract } from "@/features/map/hooks/useMapServiceContract";
import { SearchHistoryModal } from "./components/SearchHistoryModal";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { type User } from "@/types/localstorage";

/** 地図に反映する際の選択件数上限 */
const MAP_REFLECT_LIMIT = 500;

const RealEstateReceptionBookFeedPresenter: React.FC<
  IRealEstateReceptionBookFeedPresenterProps
> = (props) => {
  const {
    gridApiRef,
    handleSearch,
    setSotoRange,
    setEstimateAcreageRange,
    setEstimateLandPriceRange,
    setApproximateEstimatePriceRange,
    setResidenceOptions,
    setCommercialOptions,
    setIndustryOptions,
    setYosekiRatioRange,
    setKenpeiRatioRange,
    setAreaUsePurposeDataSource,
    setHasTrade,
    setBuildingAgeRange,
    setFarmlandAreaRange,
    setSotoFudenashi,
    filterCondition,
    importStatuses,
    sotoRange,
    estimateAcreageRange,
    estimateLandPriceRange,
    approximateEstimatePriceRange,
    residenceOptions,
    commercialOptions,
    industryOptions,
    yosekiRatioRange,
    kenpeiRatioRange,
    areaUsePurposeDataSource,
    hasTrade,
    buildingAgeRange,
    farmlandAreaRange,
    defaultAreaUsePurposeDataSource,
  } = props;

  const navigate = useNavigate();
  const isReadOnly = useReadOnlyUser();
  const {
    ownerInfoList,
    allMattersAnalysis,
    hotelSearch,
    cityPlanningAreaUsePurposeSearch,
    dormitorySearch,
  } = useFeatureFlags();

  // 登記/図面取得ダイアログ表示/非表示を決定するフラグ
  const [singleBookAcquireDialogStatus, setSingleBookAcquireDialogStatus] =
    useState<boolean>(false);

  // 登記/図面取得ダイアログで使用するデータをまとめて格納するState
  const [singleBookAcquireDialogData, setSingleBookAcquireDialogData] =
    useState<ISingleBookAcquireDialogData>({
      prefecturesId: 0,
      prefectures: "",
      address: "",
      chiban: "",
      kaokuNumber: "",
      realEstateTypeEng: "",
    });

  // feedの一覧から登記複数取得を行うためのUIのボタンの文字列、ツールチップ文字列、DataGridの選択状態を保持するStateの初期値
  const initMultipleBookAcquireData = (): IMultipleBookAcquireData => {
    return {
      buttonString: "地番・家屋番号から\n不動産登記簿を取得する",
      buttonDisabled: false,
      tooltipMessage: "",
      selectedItem: [],
    };
  };

  // feedの一覧から登記複数取得を行うためのState
  const [multipleBookAcquireData, setMultipleBookAcquireData] =
    useState<IMultipleBookAcquireData>(initMultipleBookAcquireData());

  // DataGridに行の選択状態をクリアする指示を出すState
  // setClearSelection(true)で選択状態クリア
  const [clearSelection, setClearSelection] = React.useState(false);

  // 検索履歴モーダルの状態管理
  const [searchHistoryModalOpen, setSearchHistoryModalOpen] = useState(false);

  // 検索履歴モーダルを開く
  const handleOpenSearchHistory = (): void => {
    setSearchHistoryModalOpen(true);
  };

  // 検索履歴モーダルを閉じる
  const handleCloseSearchHistory = (): void => {
    setSearchHistoryModalOpen(false);
  };

  // 検索履歴が選択された時の処理
  const handleSelectSearchHistory = (
    data: UserSearchHistoryReceptionBookResponseType
  ): void => {
    // 都道府県
    props.setPrefectures(data.prefectures.map((id) => id.toString()));
    // 市区町村
    props.setCities(data.cities.map((id) => id.toString()));

    // 法務局受付日(開始)
    props.setLegalAffairsBureauRequestDateStart(
      new Date(data.legalAffairsBureauRequestDateStart)
    );
    // 法務局受付日(終了)
    props.setLegalAffairsBureauRequestDateEnd(
      new Date(data.legalAffairsBureauRequestDateEnd)
    );
    // 登記原因
    if (data.receptionReasons) {
      props.setReceptionReasons(data.receptionReasons);
    }

    // 申請種別（単独、連先・連続）
    if (data.realEstateBookTypeTandoku) {
      props.setRealEstateBookTypeTandoku(data.realEstateBookTypeTandoku);
    }
    if (data.realEstateBookTypeRensakiRenzoku) {
      props.setRealEstateBookTypeRensakiRenzoku(
        data.realEstateBookTypeRensakiRenzoku
      );
    }

    // 不動産種別
    if (data.realEstateTypeTochi) {
      props.setRealEstateTypeTochi(data.realEstateTypeTochi);
    }
    if (data.realEstateTypeKutate) {
      props.setRealEstateTypeKutate(data.realEstateTypeKutate);
    }
    if (data.realEstateTypeTatemono) {
      props.setRealEstateTypeTatemono(data.realEstateTypeTatemono);
    }
    if (data.realEstateTypeKyotan) {
      props.setRealEstateTypeKyotan(data.realEstateTypeKyotan);
    }

    // 外筆
    if (data.sotoMin !== undefined || data.sotoMax !== undefined) {
      // 外筆なしかどうかの判定（外の範囲が0,0の場合は外筆なし）
      if (data.sotoMin === 0 && data.sotoMax === 0) {
        props.setSotoFudenashi(true);
        props.setSotoRange({ min: 0, max: 0 });
      } else {
        props.setSotoFudenashi(false);
        props.setSotoRange({
          min: data.sotoMin ?? null,
          max: data.sotoMax ?? null,
        });
      }
    }

    // 推計地積
    if (
      data.estimateAcreageMin !== undefined ||
      data.estimateAcreageMax !== undefined
    ) {
      props.setEstimateAcreageRange({
        min: data.estimateAcreageMin ?? null,
        max: data.estimateAcreageMax ?? null,
      });
    }

    // 公示価格
    if (
      data.publicLandPriceMin !== undefined ||
      data.publicLandPriceMax !== undefined
    ) {
      props.setEstimateLandPriceRange({
        min: data.publicLandPriceMin ?? null,
        max: data.publicLandPriceMax ?? null,
      });
    }

    // 概算価格
    if (
      data.approximateEstimatePriceMin !== undefined ||
      data.approximateEstimatePriceMax !== undefined
    ) {
      props.setApproximateEstimatePriceRange({
        min: data.approximateEstimatePriceMin ?? null,
        max: data.approximateEstimatePriceMax ?? null,
      });
    }

    // 用途地域（住居系）
    if (data.residenceOptions) {
      props.setResidenceOptions(data.residenceOptions);
    }

    // 用途地域（商業系）
    if (data.commercialOptions) {
      props.setCommercialOptions(data.commercialOptions);
    }

    // 用途地域（工業系）
    if (data.industryOptions) {
      props.setIndustryOptions(data.industryOptions);
    }

    // 容積率
    if (
      data.yosekiRatioMin !== undefined ||
      data.yosekiRatioMax !== undefined
    ) {
      props.setYosekiRatioRange({
        min: data.yosekiRatioMin ?? null,
        max: data.yosekiRatioMax ?? null,
      });
    }

    // 建ぺい率
    if (
      data.buildingRatioMin !== undefined ||
      data.buildingRatioMax !== undefined
    ) {
      props.setKenpeiRatioRange({
        min: data.buildingRatioMin ?? null,
        max: data.buildingRatioMax ?? null,
      });
    }

    // 用途地域データソース
    props.setAreaUsePurposeDataSource(
      data.areaUsePurposeDataSource ?? defaultAreaUsePurposeDataSource
    );

    // 建物種別
    if (data.isTowerApartment !== undefined) {
      props.setIsTowerApartment(data.isTowerApartment);
    }
    if (data.isOneRoom !== undefined) {
      props.setIsOneRoom(data.isOneRoom);
    }
    if (data.isMansion !== undefined) {
      props.setIsMansion(data.isMansion);
    }
    if (data.isApartment !== undefined) {
      props.setIsApartment(data.isApartment);
    }
    if (data.isNonResidentialBuilding !== undefined) {
      props.setIsNonResidentialBuilding(data.isNonResidentialBuilding);
    }
    if (data.isHotel !== undefined) {
      props.setIsHotel(hotelSearch ? data.isHotel : false);
    }
    if (data.isDormitory !== undefined) {
      props.setIsDormitory(dormitorySearch ? data.isDormitory : false);
    }
    if (data.isPlant !== undefined || data.isIndustrialFacility !== undefined) {
      props.setIsPlant(data.isPlant || data.isIndustrialFacility);
    }
    if (data.isRenewableEnergy !== undefined) {
      props.setIsRenewableEnergy(data.isRenewableEnergy);
    }
    if (data.isParkingLot !== undefined) {
      props.setIsParkingLot(data.isParkingLot);
    }

    // マンション名
    if (data.propertyName !== undefined) {
      props.setPropertyName(data.propertyName ?? "");
    }

    // 築年月
    if (data.buildDateStart) {
      props.setBuildDateStart(new Date(data.buildDateStart));
    }
    if (data.buildDateEnd) {
      props.setBuildDateEnd(new Date(data.buildDateEnd));
    }

    // 築年数
    if (data.buildAgeMin !== undefined || data.buildAgeMax !== undefined) {
      props.setBuildingAgeRange({
        min: data.buildAgeMin ?? null,
        max: data.buildAgeMax ?? null,
      });
    }

    // 路線名
    if (data.stationline !== null && data.stationline !== undefined) {
      props.setStationLine(data.stationline);
    }
    // 駅名
    if (data.stations) {
      const formattedStations = Object.entries(data.stations).map(
        ([id, name]) => ({
          value: id,
          label: name,
        })
      );
      props.setStationSelectedValues(formattedStations);
    }

    // 徒
    if (data.walk !== undefined) {
      props.setWalk(data.walk ?? -1);
    }

    // 農地面積
    if (
      data.farmlandAreaMin !== undefined ||
      data.farmlandAreaMax !== undefined
    ) {
      props.setFarmlandAreaRange({
        min: data.farmlandAreaMin ?? null,
        max: data.farmlandAreaMax ?? null,
      });
    }

    // 農地地目
    if (data.farmlandCategories && Array.isArray(data.farmlandCategories)) {
      props.setFarmlandCategories(data.farmlandCategories);
    }
  };

  const handleSingleBookAcquireDialogClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    setSingleBookAcquireDialogStatus(false);
  };

  // セレクトボックスが変更された時の処理
  const handleSelectChange = (
    key: string,
    event: SelectChangeEvent<number | null>,
    handleTarget: string
  ): void => {
    const value = Number(event.target.value);

    // valueが-1の場合は「指定なし」を選択したということなのでnullに変換して代入する
    if (handleTarget === "yosekiRatio") {
      const newRange = { ...yosekiRatioRange };
      newRange[key] = value === -1 ? null : value;
      setYosekiRatioRange(newRange);
    } else if (handleTarget === "kenpeiRatio") {
      const newRange = { ...kenpeiRatioRange };
      newRange[key] = value === -1 ? null : value;
      setKenpeiRatioRange(newRange);
    }
  };

  // テキストフィールドが変更されたときの処理
  const handleInputChange = (
    key: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    handleTarget: string
  ): void => {
    // 入力された値を取得
    const value: string = event.target.value;

    if (handleTarget === "soto") {
      const newRange = { ...sotoRange };
      newRange[key] = toNumber(value);
      setSotoRange(newRange);
    } else if (handleTarget === "estimateAcreage") {
      const newRange = { ...estimateAcreageRange };
      newRange[key] = toNumber(value);
      setEstimateAcreageRange(newRange);
    } else if (handleTarget === "estimateLandPrice") {
      const newRange = { ...estimateLandPriceRange };
      newRange[key] = toNumber(value);
      setEstimateLandPriceRange(newRange);
    } else if (handleTarget === "approximateEstimatePrice") {
      const newRange = { ...approximateEstimatePriceRange };
      newRange[key] = toNumber(value);
      setApproximateEstimatePriceRange(newRange);
    } else if (handleTarget === "buildingAge") {
      const newRange = { ...buildingAgeRange };
      const numberVal = toNumber(value);
      // 1未満の入力値は許可しない。
      if (numberVal != null && numberVal < 1) {
        event.target.value = "";
        return;
      }
      newRange[key] = numberVal;
      setBuildingAgeRange(newRange);
    } else if (handleTarget === "farmlandArea") {
      const newRange = { ...farmlandAreaRange };
      const numberVal = toNumber(value);
      if (numberVal != null && (numberVal < 1 || numberVal > 2147483647)) {
        event.target.value = "";
        return;
      }
      newRange[key] = numberVal;
      setFarmlandAreaRange(newRange);
    }
  };

  const handleSotohudenashiClick = (checked: boolean): void => {
    setSotoFudenashi(checked);

    if (checked) {
      setSotoRange({ min: 0, max: 0 });
    } else {
      setSotoRange((prev) => {
        if (prev.min === 0 && prev.max === 0) {
          return { min: null, max: null };
        }
        return prev;
      });
    }
  };

  function toNumber(value: string): number | null {
    if (value === "") {
      return null;
    } else {
      return Number(value);
    }
  }

  // 住居系のチェックボックスの状態を管理するステート
  const [residenceAllChecked, setResidenceAllChecked] = React.useState(false);
  // 商業系のチェックボックスの状態を管理するステート
  const [commercialAllChecked, setCommercialAllChecked] = React.useState(false);
  // 工業系のチェックボックスの状態を管理するステート
  const [industryAllChecked, setIndustryAllChecked] = React.useState(false);
  const [user] = useLocalStorage<User | undefined>("user", undefined);

  // 地図契約の権限があるかどうかを判定するフック
  const { data: mapServiceContract, isLoading: isMapServiceContractLoading } =
    useMapServiceContract();
  // ローディング中は権限がある前提で表示し、取得完了後に正確な判定を行う
  const hasMapViewingPermission = isMapServiceContractLoading
    ? true
    : mapServiceContract?.hasMapViewingPermission ?? false;

  // 地図に反映するボタンの選択件数上限チェック
  const isOverMapReflectLimit =
    multipleBookAcquireData.selectedItem.length > MAP_REFLECT_LIMIT;

  // 住居系のチェックボックスの一括処理
  const handleResidenceChangeAll = (event: boolean): void => {
    // チェックボックスの状態を反転させる
    setResidenceAllChecked(!residenceAllChecked);
    // 選択肢のチェックボックスの状態を全て同じにする
    setResidenceOptions({
      ittei: event,
      icchuko: event,
      ichiju: event,
      nitei: event,
      nichuko: event,
      niju: event,
      denju: event,
      junju: event,
    });
  };

  // 商業系のチェックボックスの一括処理
  const handleCommercialChangeAll = (event: boolean): void => {
    // チェックボックスの状態を反転させる
    setCommercialAllChecked(!commercialAllChecked);
    // 選択肢のチェックボックスの状態を全て同じにする
    setCommercialOptions({
      kinsyo: event,
      syogyo: event,
    });
  };

  // 工業系のチェックボックスの一括処理
  const handleIndustryChangeAll = (event: boolean): void => {
    // チェックボックスの状態を反転させる
    setIndustryAllChecked(!industryAllChecked);
    // 選択肢のチェックボックスの状態を全て同じにする
    setIndustryOptions({
      junko: event,
      kogyo: event,
      kosen: event,
    });
  };

  // 選択肢のチェックボックスが変更されたときの処理
  const handleOptionChange = (
    event: boolean,
    handleTarget: string,
    key: string
  ): void => {
    // 選択肢のチェックボックスが全てチェックされているかどうかを判定する
    // const isAllChecked = Object.values(options).every((value) => value);

    if (handleTarget === "residence") {
      setResidenceOptions({
        ...residenceOptions,
        [key]: event,
      });
      // 全選択がうまく機能しないので保留
      // setResidenceAllChecked(isAllChecked);
    } else if (handleTarget === "commercial") {
      setCommercialOptions({
        ...commercialOptions,
        [key]: event,
      });
      // 全選択がうまく機能しないので保留
      // setCommercialAllChecked(isAllChecked);
    } else if (handleTarget === "industry") {
      setIndustryOptions({
        ...industryOptions,
        [key]: event,
      });
      // 全選択がうまく機能しないので保留
      // setIndustryAllChecked(isAllChecked);
    }
  };

  // 地図画面を別タブで開くため、ローカルストレージに地図に反映するためのデータを保存する
  const handleMapReflectClick = (): void => {
    const handOffMessage: HandOffMessage = {
      command: "MAP_DRAW_RECEPTION_BOOKS",
      argument: multipleBookAcquireData.selectedItem,
    };
    const navigateOptions: NavigateOptions = {
      state: { handOffMessage },
    };
    const stateKey = `mapState_${Date.now()}`;
    localStorage.setItem(stateKey, JSON.stringify(navigateOptions.state));
    window.open(`/map?stateKey=${stateKey}`, "_blank", "noopener,noreferrer");
  };

  // 登記/図面取得ボタンが押された時の処理
  const handleAcquireMultipleBooksClick = (): void => {
    if (multipleBookAcquireData.selectedItem.length > 0) {
      // feedのテーブルが1件以上選択されている場合複数登記取得画面へ遷移
      navigate("/acquiremultiplebooks", {
        state: multipleBookAcquireData.selectedItem,
      });
    } else {
      // feedのテーブルが選択されていない場合単体登記取得画面へ遷移
      navigate("/acquirebook", { state: {} });
    }
  };

  // 過去のfilterConditionを保存するHook
  const prevFilterCondition = usePrevious(filterCondition);

  // 再検索が行われた場合、DataGridの選択状態やボタンの状態を保存しているStateを初期化する
  useEffect(() => {
    // 売買スクリーニングがONの状態で再検索を行うと自動で売買スクリーニングがOFFにされる
    // この場合、「売買スクリーニング」と「それ以外の検索条件」が両方同時に変化する
    // そこで、売買スクリーニング"のみ"が変化した場合は何もしないが、「それ以外の検索条件」が変化した場合はチェックの状態をクリアするようにする

    /*
      チェックボックスをクリアする判定条件
      |条件| 売買スクリーニングの前回状態との差  | 売買スクリーニング以外の前回状態との差   | 結果             |
      ---------------------------------------------------------------------------------------------
      | 1 | 売買スクリーニングが一致           | 売買スクリーニング以外が一致           | false（クリアする）|
      | 2 | 売買スクリーニングが一致           | 売買スクリーニング以外が不一致         | false（クリアする）|
      | 3 | 売買スクリーニングが不一致         | 売買スクリーニング以外が一致           | true（クリアしない）|
      | 4 | 売買スクリーニングが不一致         | 売買スクリーニング以外が不一致          | false（クリアする）|
    */

    // まず売買スクリーニングのhasTradeを取り除いたオブジェクトを生成する
    const prevFilterConditionExcludesHasTrade = (({
      hasTrade: _hasTrade,
      ...rest
    }) => rest)(prevFilterCondition);
    const filterConditionExcludesHasTrade = (({
      hasTrade: _hasTrade,
      ...rest
    }) => rest)(filterCondition);

    // 条件3であるかを判定し、当てはまる場合は何もしない
    if (
      prevFilterCondition.hasTrade !== filterCondition.hasTrade &&
      deepEqual(
        prevFilterConditionExcludesHasTrade,
        filterConditionExcludesHasTrade
      )
    ) {
      return;
    }

    // 条件1,2,4の場合はDataGridの選択状態をクリアする

    // DataGridのCustomToolbarに行の選択状態を解除する指示を送る
    setClearSelection(true);

    // 登記複数取得を行うためのStateを初期化する
    setMultipleBookAcquireData(initMultipleBookAcquireData());
  }, [filterCondition]);

  return (
    <>
      {/* 登記1件取得のダイアログ */}
      <Dialog
        open={singleBookAcquireDialogStatus}
        onClose={handleSingleBookAcquireDialogClose}
        fullWidth={true}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"登記および図面選択"}
        </DialogTitle>
        <DialogContent>
          <AcquireBookFormUI
            mode={"modal"}
            singleBookAcquireDialogData={singleBookAcquireDialogData}
            isReadOnly={isReadOnly}
            handleClose={() => {
              setSingleBookAcquireDialogStatus(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <PagePaper>
        <Accordion defaultExpanded sx={{ mb: 1 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h6">お知らせ</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ReceptionBookImportStatus importStatuses={importStatuses} />
          </AccordionDetails>
        </Accordion>

        <Box sx={{ display: "flex", mb: 3 }}>
          <MenuBookIcon sx={{ mr: 1 }} fontSize="large" />
          <PageTitle>不動産の権利異動から検索する</PageTitle>
        </Box>

        {/* 検索条件 */}
        <SearchCondition
          handleInputChange={handleInputChange}
          preventNonNumericValueWhenKeyDown={preventNonNumericValueWhenKeyDown}
          preventNonNumericValueWhenPaste={preventNonNumericValueWhenPaste}
          residenceAllChecked={residenceAllChecked}
          handleResidenceChangeAll={handleResidenceChangeAll}
          handleOptionChange={handleOptionChange}
          commercialAllChecked={commercialAllChecked}
          handleCommercialChangeAll={handleCommercialChangeAll}
          industryAllChecked={industryAllChecked}
          handleIndustryChangeAll={handleIndustryChangeAll}
          handleSelectChange={handleSelectChange}
          handleSotohudenashiClick={handleSotohudenashiClick}
          cityPlanningAreaUsePurposeSearch={!!cityPlanningAreaUsePurposeSearch}
          handleOpenSearchHistory={handleOpenSearchHistory}
          {...props}
        />

        <Box sx={{ mt: 3 }} />

        {/* 不動産登記受付帳一覧テーブル */}
        <SectionPaper>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems="center"
          >
            <SectionTitle>不動産権利異動情報一覧</SectionTitle>
            <Stack mx={2} my={2} direction="row" spacing={1}>
              {hasMapViewingPermission && (
                <Tooltip
                  title={
                    user?.isFree ? (
                      "有料プランでご利用いただける機能です"
                    ) : isOverMapReflectLimit ? (
                      `地図に反映するには選択件数を${formatNumberWithComma(
                        MAP_REFLECT_LIMIT
                      )}件以下にしてください`
                    ) : (
                      <>
                        地図に表示できるのは地番を持った受付帳データです
                        <br />
                        家屋番号の受付帳データは地図に表示されません
                      </>
                    )
                  }
                  placement="top"
                  arrow
                >
                  <span style={{ display: "inherit" }}>
                    <MuiButton
                      onClick={handleMapReflectClick}
                      variant="outlined"
                      disabled={
                        (user?.isFree ?? false) || isOverMapReflectLimit
                      }
                    >
                      地図に反映する
                    </MuiButton>
                  </span>
                </Tooltip>
              )}
              {allMattersAnalysis ? (
                <LinkButton
                  variant={ButtonVariantOption.Outlined}
                  href="/allmatters/upload"
                  sx={{ textAlign: "center" }}
                  wrapperSize={SizingWrapperStyle.INHERIT}
                >
                  全部事項
                  <br />
                  データ化
                </LinkButton>
              ) : null}
              {ownerInfoList ? (
                <LinkButton
                  variant={ButtonVariantOption.Outlined}
                  href="/ownerinfo/upload"
                  sx={{ textAlign: "center" }}
                  wrapperSize={SizingWrapperStyle.INHERIT}
                >
                  所有者事項
                  <br />
                  データ化
                </LinkButton>
              ) : null}
              <Tooltip
                title={multipleBookAcquireData.tooltipMessage}
                placement="top"
                arrow
              >
                <div style={{ display: "inherit" }}>
                  <MuiButton
                    onClick={handleAcquireMultipleBooksClick}
                    variant="outlined"
                    disabled={multipleBookAcquireData.buttonDisabled}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {multipleBookAcquireData.buttonString}
                  </MuiButton>
                </div>
              </Tooltip>
              <LinkButton
                variant={ButtonVariantOption.Outlined}
                href="/acquiremultiplebooks/upload"
                sx={{ textAlign: "center" }}
                wrapperSize={SizingWrapperStyle.INHERIT}
              >
                地番・家屋番号リストから
                <br />
                不動産登記簿を取得する
              </LinkButton>
            </Stack>
          </Stack>

          <Divider />

          <FeedGrid
            apiRef={gridApiRef}
            filterCondition={filterCondition}
            toolbarProps={{
              hasTradeState: { hasTrade, setHasTrade },
              handleSearch,
            }}
            setSingleBookAcquireDialogStatus={setSingleBookAcquireDialogStatus}
            setSingleBookAcquireDialogData={setSingleBookAcquireDialogData}
            multipleBookAcquireData={multipleBookAcquireData}
            setMultipleBookAcquireData={setMultipleBookAcquireData}
            clearSelection={clearSelection}
            setClearSelection={setClearSelection}
          />

          <Divider />

          <Box sx={{ mb: 6 }} />
        </SectionPaper>
      </PagePaper>

      {/* 検索履歴モーダル */}
      <SearchHistoryModal
        open={searchHistoryModalOpen}
        onClose={handleCloseSearchHistory}
        onSelect={handleSelectSearchHistory}
      />
    </>
  );
};

export { RealEstateReceptionBookFeedPresenter };
